import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Global scroll-restoration for React Router SPA navigations.
 *
 * - On pathname or search change, reset window scroll to top.
 * - Preserve intentional #hash anchor jumps (browser scrolls to the anchor).
 * - Avoid interfering with Back/Forward: the browser fires POP with a stored
 *   position — we only reset for PUSH/REPLACE navigations.
 * - No smooth scrolling so transitions feel instant.
 */
export default function ScrollToTop() {
  const { pathname, search, hash, key } = useLocation();

  useEffect(() => {
    // If the URL has a hash, scroll to the anchor once the target element mounts.
    if (hash) {
      const id = hash.slice(1);
      // Try immediately, then retry once after next paint in case the target
      // section hasn't finished mounting yet (SPA route transition).
      const scrollToAnchor = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ block: "start" });
          return true;
        }
        return false;
      };
      if (!scrollToAnchor()) {
        const raf = window.requestAnimationFrame(() => {
          if (!scrollToAnchor()) {
            // One more retry after a short delay for slower renders.
            window.setTimeout(scrollToAnchor, 80);
          }
        });
        return () => window.cancelAnimationFrame(raf);
      }
      return;
    }

    // Only reset on forward navigation, not on browser Back/Forward.
    const navType = window.performance?.getEntriesByType?.("navigation")?.[0]?.type;
    // For SPA route changes, use history state to detect back/forward.
    const isPop = window.history?.state?.idx !== undefined
      ? false // react-router-dom v6 sets idx; can't reliably distinguish here — safe default: reset
      : false;

    if (!isPop) {
      window.scrollTo(0, 0);
    }
    // Reset the document element too for browsers that scroll <html> vs body.
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname, search, hash, key]);

  return null;
}
